'use client';

import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
// import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Title from '@/components/ui/Title';
import { Pagination } from '@/components/ui/pagination';
import { Pencil, Trash2, X } from 'lucide-react';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import type {
  EvergreenTimelineEvent,
  EvergreenChatAuthorRole,
  ParticipationStatusDto,
} from '@/features/webinar/types';
import {
  deleteEvergreenChatMessages,
  deleteEvergreenTimeline,
  fetchEvergreenChatConfiguration,
} from '@/features/webinar/webinarThunks';
import {
  isEditableManualMessage,
  readChatMessageContent,
  readChatMessageMetadata,
  resolveChatMessageLabel,
  type ChatMessageLabel,
} from '@/lib/evergreenManualChatMessage';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';
// import Loader from '@/components/ui/Loader';

type MessageRow = {
  id: string;
  message: string;
  name: string;
  timestamp: string;
  type: string;
  role: EvergreenChatAuthorRole;
  label: ChatMessageLabel | null;
  event: EvergreenTimelineEvent;
};

type ListMessagesProps = {
  onRowClick?: (row: MessageRow) => void;
  readOnly?: boolean;
  editingMessageId?: number | null;
  onEditMessage?: (event: EvergreenTimelineEvent) => void;
};

function formatSecondsToTimestamp(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

function roleToBadgeVariant(
  role: EvergreenChatAuthorRole,
): 'live' | 'evergreen' | 'incomplete' | null {
  switch (role) {
    case 'MODERATOR':
      return 'live';
    case 'ATTENDEE':
      return 'evergreen';
    case 'PRESENTER':
      return 'incomplete';
    default:
      return null;
  }
}

function eventToMessageRow(
  event: EvergreenTimelineEvent,
  participationStatuses: readonly ParticipationStatusDto[],
): MessageRow {
  const { author, role } = readChatMessageMetadata(event.metadata);
  return {
    id: String(event.id),
    message: readChatMessageContent(event),
    name: author,
    timestamp: formatSecondsToTimestamp(event.startOffsetSeconds),
    type: '',
    role,
    label: resolveChatMessageLabel(event.metadata, participationStatuses),
    event,
  };
}

const MESSAGES_PAGE_SIZE = 40;

export default function ListMessages({
  onRowClick,
  readOnly,
  editingMessageId = null,
  onEditMessage,
}: ListMessagesProps) {
  const locked = readOnly === true;
  const { t } = useTranslation('common');
  const { t: tCW } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = typeof webinarIdParam === 'string' ? parseInt(webinarIdParam, 10) : undefined;
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const {
    currentEvergreenChatConfiguration,
    evergreenChatConfigurationLoading,
    participationStatuses,
    participationStatusesLoading,
    participationStatusesError,
  } = useAppSelector((state: RootState) => state.webinar);
  const labelsReady = !participationStatusesLoading && participationStatusesError == null;
  const [rowSelection, setRowSelection] = React.useState({});
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [pageNumber, setPageNumber] = React.useState(0);

  const handleDeleteMessage = React.useCallback(
    async (eventId: string) => {
      if (locked) return;
      if (!workspaceId || !webinarId || isNaN(webinarId)) return;
      const id = parseInt(eventId, 10);
      if (isNaN(id)) return;
      setDeletingId(eventId);
      const result = await dispatch(deleteEvergreenTimeline({ workspaceId, webinarId, ids: [id] }));
      setDeletingId(null);
      if (deleteEvergreenTimeline.fulfilled.match(result)) {
        toast.success(tCW('chatEvergreenForm.success.messageDeleted'));
        await dispatch(fetchEvergreenChatConfiguration({ workspaceId, webinarId }));
      } else if (deleteEvergreenTimeline.rejected.match(result)) {
        const error = result.payload as { message?: string } | undefined;
        const msg = error?.message
          ? t(error.message, 'Une erreur est survenue.')
          : t('errors.generic', 'Une erreur est survenue.');
        toast.error(msg);
      }
    },
    [dispatch, workspaceId, webinarId, t, locked],
  );

  const data = React.useMemo<MessageRow[]>(() => {
    const messages = currentEvergreenChatConfiguration?.messages ?? [];
    return messages
      .filter((e) => e.eventType === 'SUPPORT_MESSAGE')
      .sort(
        (a, b) => a.startOffsetSeconds - b.startOffsetSeconds || a.displayOrder - b.displayOrder,
      )
      .map((event) => eventToMessageRow(event, participationStatuses));
  }, [currentEvergreenChatConfiguration, participationStatuses]);

  const totalPages = Math.max(1, Math.ceil(data.length / MESSAGES_PAGE_SIZE));
  const paginatedData = React.useMemo(
    () => data.slice(pageNumber * MESSAGES_PAGE_SIZE, (pageNumber + 1) * MESSAGES_PAGE_SIZE),
    [data, pageNumber],
  );

  React.useEffect(() => {
    if (pageNumber >= totalPages && totalPages > 0) {
      setPageNumber(Math.max(0, totalPages - 1));
    }
  }, [pageNumber, totalPages]);

  const canDelete = workspaceId != null && webinarId != null && !isNaN(webinarId);
  const deleteDisabled =
    locked || !canDelete || evergreenChatConfigurationLoading || isDeleting || deletingId != null;

  const columns = React.useMemo<ColumnDef<MessageRow>[]>(
    () => [
      {
        accessorKey: 'message',
        header: tCW('chatEvergreenForm.list.colMessage'),
        cell: ({ row }) => <span className="text-secondary">{row.getValue('message')}</span>,
      },
      {
        accessorKey: 'name',
        header: tCW('chatEvergreenForm.list.colName'),
        cell: ({ row }) => <span className="text-black font-medium">{row.getValue('name')}</span>,
      },
      {
        accessorKey: 'timestamp',
        header: tCW('chatEvergreenForm.list.colTimestamp'),
        cell: ({ row }) => <span className="text-secondary">{row.getValue('timestamp')}</span>,
      },
      {
        accessorKey: 'type',
        header: tCW('chatEvergreenForm.list.colType'),
        cell: ({ row }) => {
          const role = row.original.role as EvergreenChatAuthorRole;
          const roleLabels: Record<string, string> = {
            PRESENTER: tCW('chatEvergreenForm.roles.presenter'),
            MODERATOR: tCW('chatEvergreenForm.roles.moderator'),
            SPEAKER: tCW('chatEvergreenForm.roles.speaker'),
            ATTENDEE: tCW('chatEvergreenForm.roles.attendee'),
          };
          const label = roleLabels[role] ?? role;
          const variant = roleToBadgeVariant(role);

          if (variant) {
            return (
              <Badge variant={variant} className="capitalize">
                {label}
              </Badge>
            );
          }

          return <span className="text-secondary">{label}</span>;
        },
      },
      {
        id: 'label',
        header: tCW('chatEvergreenForm.list.colLabel'),
        cell: ({ row }) => {
          if (!labelsReady) return null;
          const messageLabel = row.original.label;
          if (!messageLabel) {
            return (
              <span className="text-secondary italic">{tCW('chatEvergreenForm.list.noLabel')}</span>
            );
          }
          return (
            <Badge variant="draft">
              {messageLabel.color && (
                <span
                  className="size-2 rounded-full border border-black/10"
                  style={{ backgroundColor: messageLabel.color }}
                />
              )}
              {messageLabel.label}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const id = row.original.id;
          const isDeletingRow = deletingId === id;
          const sourceEvent = row.original.event;
          const isEditingRow = editingMessageId === sourceEvent.id;
          return (
            <div className="flex items-center gap-1">
              {onEditMessage && isEditableManualMessage(sourceEvent) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditMessage(sourceEvent);
                  }}
                  disabled={deleteDisabled}
                  className={cn(
                    'p-1 rounded cursor-pointer text-secondary disabled:opacity-50 disabled:cursor-not-allowed',
                    isEditingRow && 'text-[#2356CC]',
                  )}
                  aria-label={tCW('chatEvergreenForm.list.editAriaLabel')}
                  aria-pressed={isEditingRow}
                >
                  <Pencil size={18} />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMessage(id);
                }}
                disabled={isDeletingRow || deleteDisabled}
                className="p-1 rounded cursor-pointer text-error disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={tCW('chatEvergreenForm.list.deleteAriaLabel')}
              >
                <X size={18} />
              </button>
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [
      handleDeleteMessage,
      deletingId,
      deleteDisabled,
      tCW,
      editingMessageId,
      onEditMessage,
      labelsReady,
    ],
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  const handleDeleteAllMessages = async () => {
    if (locked) return;
    if (!workspaceId || !webinarId || isNaN(webinarId)) return;
    setIsDeleting(true);
    const result = await dispatch(deleteEvergreenChatMessages({ workspaceId, webinarId }));
    setIsDeleting(false);
    if (deleteEvergreenChatMessages.fulfilled.match(result)) {
      toast.success(tCW('chatEvergreenForm.success.allDeleted'));
    } else if (deleteEvergreenChatMessages.rejected.match(result)) {
      const error = result.payload as { message?: string } | undefined;
      const msg = error?.message
        ? t(error.message, 'Une erreur est survenue.')
        : t('errors.generic', 'Une erreur est survenue.');
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-8">
      <div className={cn('space-y-8', locked && 'pointer-events-none select-none opacity-90')}>
        <div className="flex items-center justify-between">
          <Title level={2} className="text-secondary">
            {tCW('chatEvergreenForm.list.title')}
          </Title>
          <div className="flex items-center gap-2 ">
            <button
              type="button"
              onClick={handleDeleteAllMessages}
              disabled={deleteDisabled}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={24} className="text-secondary" />
              <Label className="text-secondary cursor-pointer">
                {isDeleting
                  ? tCW('chatEvergreenForm.list.deleting')
                  : tCW('chatEvergreenForm.list.deleteAll')}
              </Label>
            </button>
          </div>
        </div>

        <div className="overflow-hidden border border-[#FBFBFB] rounded-2xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-secondary">
                    {tCW('chatEvergreenForm.list.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={onRowClick ? 'cursor-pointer' : undefined}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      {data.length > 0 && (
        <div className="w-full flex justify-center">
          <Pagination
            currentPage={pageNumber}
            totalPages={totalPages}
            onPageChange={setPageNumber}
          />
        </div>
      )}
    </div>
  );
}
